import { useState } from 'react';

import { FileCheck2Icon, FileX2Icon, XIcon } from 'lucide-react';

import Loading from '../../../feedback/loading.jsx';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../ui/alert-dialog.jsx';
import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import { Label } from '../../../ui/label.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select.jsx';
import { Textarea } from '../../../ui/textarea.jsx';

const INVOICE_AUDIT_TYPES = {
  approve: {
    buttonName: 'Aprovar',
    buttonVariant: 'success',
    buttonIcon: <FileCheck2Icon />,
    description: 'O registo selecionado será aprovado.',
    loadingMessage: 'A aprovar registo',
    errorMessage: {
      message: 'Erro ao aprovar registo...',
      description: 'Tente novamente mais tarde',
    },
    checkbox: {
      message:
        'Eu concordo que esta ação tem consequências irreversíveis e que mesmo assim quero realizá-la.',
    },
  },
  reject: {
    buttonName: 'Rejeitar',
    buttonVariant: 'destructive',
    buttonIcon: <FileX2Icon />,
    description: 'O registo selecionado será rejeitado.',
    loadingMessage: 'A rejeitar registo',
    errorMessage: {
      message: 'Erro ao rejeitar registo...',
      description: 'Tente novamente mais tarde',
    },
    checkbox: {
      message:
        'Eu concordo que esta ação tem consequências irreversíveis e que mesmo assim quero realizá-la.',
    },
    reason: {
      options: [
        'Identificador da fatura inválido',
        'Valor da fatura inválido',
        'Fatura duplicada',
        'Loja errada',
        'Outro',
      ],
    },
  },
};

export const DialogInvoiceAudit = ({
  button,
  disabled,
  action,
  description,
  type = 'approve',
}) => {
  const selectedType = INVOICE_AUDIT_TYPES[type];

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState({
    select: '',
    text: '',
  });

  if (!selectedType) {
    return 'Invalid dialog confirmation type';
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {button || (
          <Button disabled={disabled} variant={selectedType.buttonVariant}>
            {selectedType.buttonIcon}
            {selectedType.buttonName}
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent>
        {isLoading ? (
          <div className="flex w-full justify-center">
            <Loading message={selectedType.loadingMessage} />
          </div>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Tem a certeza?</AlertDialogTitle>
              <AlertDialogDescription>
                {description || selectedType.description}
              </AlertDialogDescription>
              <AlertDialogDescription className="font-bold text-red-500">
                Esta ação é irreversível.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {selectedType.checkbox && (
              <div className="flex flex-row items-start space-x-2">
                <Checkbox
                  className="mt-0.5"
                  checked={checked}
                  onCheckedChange={setChecked}
                />
                <AlertDialogDescription className="space-y-0 text-sm">
                  Eu concordo que esta ação tem consequências irreversíveis e
                  que mesmo assim quero realizá-la.
                </AlertDialogDescription>
              </div>
            )}
            {selectedType.reason && (
              <>
                <Label>Motivo</Label>
                <Select
                  onValueChange={(e) => setReason({ select: e })}
                  value={reason.select}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      'Identificador da fatura inválido',
                      'Data da fatura inválida',
                      'Valor da fatura inválido',
                      'Nº de fatura não coincide com a fatura registada',
                      'Fatura ilegível',
                      'Fatura duplicada',
                      'Loja errada',
                      'Outro',
                    ].map((item, i) => (
                      <SelectItem key={i} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {reason.select === 'Outro' && (
                  <Textarea
                    placeholder="Escreva o motivo da rejeição"
                    value={reason.text}
                    onChange={(e) =>
                      setReason((p) => ({ ...p, text: e.target.value }))
                    }
                  />
                )}
              </>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>
                <XIcon />
                Cancelar
              </AlertDialogCancel>
              <Button
                disabled={
                  (selectedType.checkbox && !checked) ||
                  (selectedType.reason && !reason.select) ||
                  (reason.select === 'Outro' && !reason.text)
                }
                variant={selectedType.buttonVariant}
                onClick={async (event) => {
                  event.preventDefault();

                  setIsLoading(true);

                  if (action) {
                    await action(
                      reason.select === 'Outro' ? reason.text : reason.select,
                    );
                  }

                  setOpen(false);
                  setIsLoading(false);
                }}
              >
                {selectedType.buttonIcon}
                {selectedType.buttonName}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
